import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Sparkles, Shield, TrendingUp } from "lucide-react";

const DISMISS_KEY = "tyrematch_ai_nudge_dismissed";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isDismissed(requestId) {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    const ts = parsed?.[requestId];
    if (!ts) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function markDismissed(requestId) {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[requestId] = Date.now();
    localStorage.setItem(DISMISS_KEY, JSON.stringify(parsed));
  } catch {
    /* ignore */
  }
}

/**
 * One-time popup that nudges the customer to use the AI Recommendation
 * engine once their request has accumulated 2+ shop quotes. Picks the
 * first eligible request (most-quoted first) and shows a single dialog.
 *
 * Dismissal is per-request_id and persists for 7 days in localStorage,
 * so the customer isn't pestered every page load. Clicking "Compare
 * with AI" calls onOpenQuotes(request) which the dashboard wires up to
 * open the Compare Quotes dialog (with the AI button right there).
 */
export function AIRecommendationNudge({ requests, onOpenQuotes }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);

  useEffect(() => {
    if (!requests || requests.length === 0) return;
    // Fire only when we're reasonably confident the customer has the
    // full picture, so they don't run the AI engine prematurely and
    // then run it AGAIN once more shops respond. Eligible iff:
    //   • at least 3 distinct shops have quoted (proxies "most of the
    //     market has weighed in"), OR
    //   • request is within 24 hours of expiry (decision must be made)
    // Plus the obvious guards: still actionable, not already dismissed.
    const SKIP_STATUSES = new Set(["completed", "cancelled", "deleted"]);
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();

    const eligible = requests
      .filter((r) => {
        if (SKIP_STATUSES.has(r.request_status)) return false;
        if (r.has_accepted_quote) return false;
        if (isDismissed(r.request_id)) return false;
        if ((r.quotes_received || 0) < 2) return false;

        // Distinct-shop check. Backend now returns `distinct_shops`
        // alongside `quotes_received` — we require ≥3 distinct shops
        // (i.e. "more than 2 shops have quoted"). A single shop
        // submitting multiple options does NOT trip the threshold.
        const enoughBreadth = (r.distinct_shops || 0) >= 3;

        // Expiry-imminent check — still has time left AND ≤24h until
        // it auto-expires. Already-expired requests do NOT trigger the
        // nudge (the customer can still open the dialog and click the
        // AI button manually if they want).
        let closeToExpiry = false;
        if (r.expiry_time) {
          const expMs = new Date(r.expiry_time).getTime();
          if (!Number.isNaN(expMs)) {
            const msLeft = expMs - now;
            closeToExpiry = msLeft > 0 && msLeft <= ONE_DAY_MS;
          }
        }

        return enoughBreadth || closeToExpiry;
      })
      .sort((a, b) => (b.quotes_received || 0) - (a.quotes_received || 0));

    if (eligible.length > 0) {
      setTarget(eligible[0]);
      // Tiny delay so it doesn't fire mid-page-load and looks intentional
      const t = setTimeout(() => setOpen(true), 700);
      return () => clearTimeout(t);
    }
  }, [requests]);

  const handleDismiss = () => {
    if (target?.request_id) markDismissed(target.request_id);
    setOpen(false);
  };

  const handleViewWithAI = () => {
    if (target?.request_id) markDismissed(target.request_id);
    setOpen(false);
    if (target && onOpenQuotes) onOpenQuotes(target, { openAi: true });
  };

  if (!target) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleDismiss();
        else setOpen(true);
      }}
    >
      <DialogContent
        className="max-w-md"
        data-testid="ai-recommendation-nudge"
      >
        <DialogHeader>
          <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center mb-3">
            <Sparkles className="h-7 w-7 text-white" />
          </div>
          <DialogTitle className="text-center font-heading text-xl">
            {target.quotes_received} quotes received — let AI help you pick
          </DialogTitle>
          <DialogDescription className="text-center text-sm">
            Request{" "}
            <span className="font-semibold text-foreground">
              {target.request_number || `#${target.request_id}`}
            </span>{" "}
            has multiple quotes. Our AI compares them against your vehicle's
            safety specs, brand tier and your stated priority — in seconds.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 my-2">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <Shield className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <p className="text-xs leading-snug">
              <span className="font-semibold">Safety-first:</span> flags any
              tyre that's under-spec for your vehicle's load/speed rating.
            </p>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-purple-500/5 border border-purple-500/20">
            <TrendingUp className="h-4 w-4 text-purple-500 mt-0.5 shrink-0" />
            <p className="text-xs leading-snug">
              <span className="font-semibold">Best value:</span> ranks every
              quote with a transparent score and explains the top pick.
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button
            onClick={handleViewWithAI}
            className="w-full bg-gradient-to-r from-primary to-purple-500 text-white hover:opacity-90 font-semibold"
            data-testid="ai-nudge-try-btn"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Compare with AI
            <span className="ml-2 px-1.5 py-0.5 rounded bg-white/25 text-[9px] font-bold tracking-wider">
              BETA
            </span>
          </Button>
          <Button
            variant="ghost"
            onClick={handleDismiss}
            className="w-full text-xs text-muted-foreground hover:text-foreground"
            data-testid="ai-nudge-dismiss-btn"
          >
            Maybe later
          </Button>
        </DialogFooter>

        <p className="text-[10px] text-muted-foreground text-center mt-1">
          Beta · Always verify safety-critical specs with your shop.
        </p>
      </DialogContent>
    </Dialog>
  );
}

export default AIRecommendationNudge;
